import { Avatar, Badge, Box, Tooltip, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { CircleTier } from '../store/api/usersApi';
import { CIRCLE_LOGO, CIRCLE_LABEL, CIRCLE_COLOR } from '../assets/circleAssets';

/**
 * Avatar de un usuario con badge del círculo de pertenencia más alto otorgado
 * (§6.3). El badge solo aparece si `topGrantedCircle` está seteado en el User.
 * Si no, se renderiza un Avatar plano.
 *
 * Tamaño del badge se escala con el tamaño del avatar.
 */

export interface AgentAvatarProps {
  src?:      string;
  initials?: string;
  size?:     number;
  /** Color/bg del avatar base cuando no hay imagen. */
  baseColor?: string;
  /** Tier del círculo otorgado — si está, se renderiza el badge. */
  tier?:      CircleTier;
  /**
   * Proporción del badge respecto del avatar. El default (0.5) es el del CRM, donde
   * los avatares son chicos y el logo tiene que leerse igual. En la página pública del
   * asesor, con una foto grande, ese mismo 50% tapa media cara: ahí se baja.
   */
  badgeScale?: number;
  sx?:        SxProps<Theme>;
}

export function AgentAvatar({
  src, initials, size = 48, baseColor, tier, sx, badgeScale = 0.5,
}: AgentAvatarProps) {
  const avatar = (
    <Avatar
      src={src}
      sx={{
        width: size, height: size,
        fontSize: size * 0.36,
        fontWeight: 800,
        ...(baseColor && {
          bgcolor: alpha(baseColor, 0.18),
          color:   baseColor,
          border:  `2px solid ${alpha(baseColor, 0.4)}`,
        }),
        ...sx,
      }}
    >
      {initials}
    </Avatar>
  );

  if (!tier) return avatar;

  const badgeSize = Math.max(18, Math.round(size * badgeScale));
  const color = CIRCLE_COLOR[tier];

  return (
    <Tooltip title={`Círculo ${CIRCLE_LABEL[tier]}`}>
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={
          <Box
            component="img"
            src={CIRCLE_LOGO[tier]}
            alt={`Círculo ${CIRCLE_LABEL[tier]}`}
            sx={{
              width: badgeSize, height: badgeSize,
              borderRadius: '50%',
              objectFit: 'contain',
              bgcolor: 'background.paper',
              filter: `drop-shadow(0 2px 6px ${alpha(color, 0.45)})`,
            }}
          />
        }
      >
        {avatar}
      </Badge>
    </Tooltip>
  );
}
